import type { TradeShowEvent, TradeShowPriceCheck } from './tradeShowTypes'

/**
 * VRG-673 — the pure half of the Trade shows page: how a CSV row becomes table cells.
 * Pinned by `apps/platform/test/trade-shows.guard.spec.ts`.
 *
 * THE ONE RULE: A DATE IS NEVER SHOWN AS MORE CERTAIN THAN THE RESEARCH. The CSV carries two
 * honesty fields, and both reach the screen:
 *   - `date_precision`: `exact`, `exact/approx` (a specific date the research could not pin to
 *     an organizer page), `approx` (no date at all — only a typical month), `exact/conflict`
 *     (sources disagree). Anything but `exact` renders with a leading "~" and an "Approx" or
 *     "Conflict" marker, and an `approx` row shows the researcher's own words instead of a date.
 *   - `date_status`: `confirmed` on the organizer's page, or `unconfirmed`.
 * A tidy "Nov 6-13" for a row whose source says "~Nov 6-13, 2026 (sources conflict)" would be
 * the dashboard inventing precision; the README's own instruction is "verify before spending".
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const parseIso = (iso: string): { y: number; m: number; d: number } | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) return null
  return { y: Number(match[1]), m: Number(match[2]) - 1, d: Number(match[3]) }
}

export type DateCertainty = 'exact' | 'approx' | 'conflict' | 'unknown'

export const dateCertainty = (e: Pick<TradeShowEvent, 'date_precision' | 'start_date'>): DateCertainty => {
  if (!parseIso(e.start_date)) return 'unknown'
  if (e.date_precision === 'exact') return 'exact'
  if (e.date_precision.includes('conflict')) return 'conflict'
  return 'approx'
}

/**
 * "Oct 7, 2026", "Oct 8-11, 2026", "Dec 30, 2026 - Jan 2, 2027"; prefixed "~" unless exact.
 * A row with no start date returns its raw text ("2027 not found"), never a guessed date.
 */
export const formatEventDates = (
  e: Pick<TradeShowEvent, 'start_date' | 'end_date' | 'date_precision' | 'next_dates_raw' | 'typical_month'>,
): string => {
  const s = parseIso(e.start_date)
  if (!s) {
    const raw = e.next_dates_raw || 'No date found'
    return e.typical_month ? `${raw} (usually ${e.typical_month})` : raw
  }
  const end = parseIso(e.end_date) ?? s
  let text: string
  if (end.y === s.y && end.m === s.m && end.d === s.d) text = `${MONTHS[s.m]} ${s.d}, ${s.y}`
  else if (end.y === s.y && end.m === s.m) text = `${MONTHS[s.m]} ${s.d}-${end.d}, ${s.y}`
  else if (end.y === s.y) text = `${MONTHS[s.m]} ${s.d} - ${MONTHS[end.m]} ${end.d}, ${s.y}`
  else text = `${MONTHS[s.m]} ${s.d}, ${s.y} - ${MONTHS[end.m]} ${end.d}, ${end.y}`
  return dateCertainty(e) === 'exact' ? text : `~${text}`
}

/**
 * The month filter's value: "2026-10" style for a dated row (sorts and groups correctly across
 * the year boundary), and "Undated" for a row with no announced date.
 */
export const eventMonthKey = (e: Pick<TradeShowEvent, 'start_date'>): string => {
  const s = parseIso(e.start_date)
  return s ? `${s.y}-${String(s.m + 1).padStart(2, '0')}` : 'Undated'
}

/** "2026-10" → "Oct 2026"; anything else passes through. */
export const monthLabel = (key: string): string => {
  const match = /^(\d{4})-(\d{2})$/.exec(key)
  return match ? `${MONTHS[Number(match[2]) - 1]} ${match[1]}` : key
}

export type FitKey = 'small' | 'mixed' | 'large' | 'unrated'

/** The CSV's emoji fit key, as a filterable value. The judgment is the researcher's, not sourced. */
export const fitKey = (fit: string): FitKey =>
  fit.includes('🟢') ? 'small' : fit.includes('🟡') ? 'mixed' : fit.includes('🔴') ? 'large' : 'unrated'

/** One price cell from a low/high pair: "USD 50 to USD 725", "free", or "Not published". */
export const formatPriceRange = (low: string, high: string): string => {
  const clean = (v: string) => (v && v.toLowerCase() !== 'not published' ? v : '')
  const lo = clean(low)
  const hi = clean(high)
  if (lo && hi && lo !== hi) return `${lo} to ${hi}`
  return lo || hi || 'Not published'
}

/** `plan_to_attend` as a filter value. Only "yes" counts; the column is read-only in v1. */
export const planKey = (plan: string): 'yes' | 'not_marked' =>
  plan.trim().toLowerCase() === 'yes' ? 'yes' : 'not_marked'

/**
 * "2026-10-05" → "Oct 5, 2026", parsed as a CALENDAR DAY. Not `new Date(iso)`: that reads the
 * string as UTC midnight, which renders as the day before anywhere west of Greenwich.
 */
export const formatIsoDay = (iso: string): string => {
  const d = parseIso(iso)
  return d ? `${MONTHS[d.m]} ${d.d}, ${d.y}` : iso
}

/** The `price_history.csv` field names, as the page labels them. Unknown fields pass through. */
export const HISTORY_FIELD_LABELS: Readonly<Record<string, string>> = {
  ticket_low: 'Ticket from',
  ticket_high: 'Ticket to',
  booth_price: 'Booth',
  next_dates_raw: 'Dates',
  date_status: 'Date status',
}

export interface PriceCheckChange {
  checked_on: string
  field: string
  value: string
  previous_value: string
}

export interface PriceCheckSummary {
  /** The latest ISO day this event was checked: events.csv's `price_checked_on` or a logged day. */
  lastCheckedOn: string
  /** Every logged change after the first value per field, newest first. */
  changes: PriceCheckChange[]
  /** ISO day of the newest change, or null when nothing has changed since the baseline. */
  lastChangedOn: string | null
}

const isIsoDay = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s)

/**
 * When an event was last checked, and what changed since its first logged values.
 *
 * `make_price_history.py` appends a row per (event, field) only when the value differs from the
 * last one logged, so the FIRST row for a field is its baseline, not a change, and every later row
 * for that field IS a change. A check that found nothing new leaves no row at all, which is why
 * `price_checked_on` from events.csv also counts towards "last checked".
 *
 * Deliberately not keyed on `previous_value` being non-empty: the script writes `prev or ""`, so a
 * field that went from blank to a price would carry an empty previous value and read as a baseline.
 */
export const priceCheckSummary = (
  e: Pick<TradeShowEvent, 'price_checked_on'>,
  checks: readonly TradeShowPriceCheck[] = [],
): PriceCheckSummary => {
  const ordered = [...checks].sort((a, b) => a.checked_on.localeCompare(b.checked_on))
  const seen = new Set<string>()
  const changes: PriceCheckChange[] = []
  for (const c of ordered) {
    for (const v of c.values) {
      if (seen.has(v.field)) changes.push({ checked_on: c.checked_on, ...v })
      else seen.add(v.field)
    }
  }
  changes.reverse()
  const days = [e.price_checked_on, ...ordered.map((c) => c.checked_on)].filter(isIsoDay).sort()
  return {
    lastCheckedOn: days[days.length - 1] ?? '',
    changes,
    lastChangedOn: changes[0]?.checked_on ?? null,
  }
}
